import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  ShieldAlert,
  Radio,
  Monitor,
  Smartphone,
  Tablet,
  TrendingUp,
} from "lucide-react";
import { checkAdmin, getSignupStats } from "@/lib/admin.functions";
import { useLivePresence } from "@/hooks/use-presence";
import type { DeviceCategory } from "@/lib/presence";

export const Route = createFileRoute("/admin/live")({
  component: AdminLive,
  head: () => ({
    meta: [
      { title: "Live visitors • The Quantum Minds admin" },
      {
        name: "description",
        content: "Real-time count of visitors currently connected to The Quantum Minds.",
      },
      { property: "og:title", content: "Live visitors • The Quantum Minds admin" },
      {
        property: "og:description",
        content: "Real-time count of visitors currently connected to The Quantum Minds.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  errorComponent: ({ error }: { error: unknown }) => (
    <div role="alert" className="p-6 text-sm text-destructive">
      {error instanceof Error ? error.message : String(error)}
    </div>
  ),
});

const deviceIcon: Record<DeviceCategory, typeof Monitor> = {
  mobile: Smartphone,
  tablet: Tablet,
  desktop: Monitor,
};

function since(iso: string): string {
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

const nf = new Intl.NumberFormat("en-IN");

function AdminLive() {
  const isAdminFn = useServerFn(checkAdmin);
  const adminQuery = useQuery({
    queryKey: ["admin-check"],
    queryFn: () => isAdminFn({}),
    retry: false,
    staleTime: 60_000,
  });
  const isAdmin = adminQuery.data?.isAdmin === true;
  const { connected, visitors, total, members, guests } = useLivePresence();
  const signupsFn = useServerFn(getSignupStats);
  const signupsQuery = useQuery({
    queryKey: ["admin-signup-stats"],
    queryFn: () => signupsFn({}),
    enabled: isAdmin,
    retry: false,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (adminQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-8 w-8 text-destructive" />
        <h1 className="text-lg font-semibold text-foreground">Admins only</h1>
        <p className="text-sm text-muted-foreground">
          The live visitor console is restricted to admin accounts, and access is verified on the
          server.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 p-4 pb-24">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Live visitors</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Currently connected browser sessions, updated in real time. One session counts once no
          matter how many pages they visit.
        </p>
      </header>

      <section className="rounded-2xl border border-border/60 bg-card/60 p-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <span
            className={
              connected
                ? "h-2 w-2 animate-pulse rounded-full bg-emerald-500"
                : "h-2 w-2 rounded-full bg-muted-foreground/50"
            }
          />
          Live Users
        </div>
        <p className="mt-2 text-4xl font-semibold text-foreground">
          🟢 {total}
          <span className="ml-2 align-middle text-sm font-normal text-muted-foreground">
            Online Now
          </span>
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Logged in" value={members} />
          <Stat label="Guests" value={guests} />
          <Stat label="Total online" value={total} />
        </div>
      </section>

      <SignupSection
        stats={signupsQuery.data}
        loading={signupsQuery.isPending}
        error={signupsQuery.error instanceof Error ? signupsQuery.error.message : null}
      />

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Session details</h2>
        {visitors.length === 0 ? (
          <p className="rounded-2xl border border-border/60 bg-card/40 p-4 text-sm text-muted-foreground">
            No visitors connected right now.
          </p>
        ) : (
          <ul className="space-y-2">
            {visitors
              .slice()
              .sort((a, b) => a.connectedSince.localeCompare(b.connectedSince))
              .map((v) => {
                const Icon = deviceIcon[v.device] ?? Monitor;
                return (
                  <li
                    key={v.sessionId}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-border/60 bg-card/60 px-3 py-2.5 text-sm"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-mono text-xs text-foreground">{v.path}</span>
                    <span
                      className={
                        v.kind === "member"
                          ? "rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary"
                          : "rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
                      }
                    >
                      {v.kind === "member" ? "Logged in" : "Guest"}
                    </span>
                    <span className="text-[11px] capitalize text-muted-foreground">{v.device}</span>
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      {since(v.connectedSince)} connected · updated {since(v.updatedAt)} ago
                    </span>
                  </li>
                );
              })}
          </ul>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/40 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

function SignupSection({
  stats,
  loading,
  error,
}: {
  stats: import("@/lib/admin.functions").SignupStats | undefined;
  loading: boolean;
  error: string | null;
}) {
  const [range, setRange] = useState<7 | 30>(7);

  return (
    <section className="rounded-2xl border border-border/60 bg-card/60 p-5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <TrendingUp className="h-3.5 w-3.5" />
        Total signups
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading signup data…
        </div>
      ) : error || !stats ? (
        <p className="mt-3 text-sm text-destructive">
          {error === "NOT_ADMIN" ? "Admins only." : (error ?? "Could not load signup stats.")}
        </p>
      ) : (
        <>
          <p className="mt-2 text-4xl font-semibold text-foreground">
            📈 {nf.format(stats.total)}
            <span className="ml-2 align-middle text-sm font-normal text-muted-foreground">
              registered accounts
            </span>
          </p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Today" value={stats.today} />
            <Stat label="Last 7 days" value={stats.last7} />
            <Stat label="Last 30 days" value={stats.last30} />
          </div>

          <div className="mt-5 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">User growth</h3>
            <div className="flex rounded-lg border border-border/60 p-0.5">
              {([7, 30] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={
                    range === r
                      ? "rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground"
                      : "rounded-md px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
                  }
                >
                  {r}d
                </button>
              ))}
            </div>
          </div>
          <GrowthChart data={stats.daily.slice(-range)} />
        </>
      )}
    </section>
  );
}

function GrowthChart({ data }: { data: { date: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="mt-3">
      <div className="flex h-32 items-end gap-[3px]">
        {data.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.count} signups`}
            className="flex-1 rounded-t bg-primary/70 transition-[height] hover:bg-primary"
            style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] text-muted-foreground">
        <span>{data[0]?.date ?? ""}</span>
        <span>peak {max}</span>
        <span>{data.at(-1)?.date ?? ""}</span>
      </div>
    </div>
  );
}
